import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { AppAccessClipboardNotification } from '../components/AppAccessPasteSnackbar';
import { AppAccess, DashboardSelectorSubmoduleItem } from '../types/dashboardSelector';
import { SubmoduleFilterRegistry } from './useSubmoduleFilterRegistry';
import { isPasteAllowed, translateFilters } from '../utils/utils';

type UseAppAccessClipboardParams = {
  setSelectedSubmodules: Dispatch<SetStateAction<DashboardSelectorSubmoduleItem[]>>;
  filterRegistry?: SubmoduleFilterRegistry;
  onPasteError?: (message: string) => void;
};

type PreviousPasteSnapshot = {
  submoduleId: string;
  submodule: DashboardSelectorSubmoduleItem;
  previousAppAccess?: AppAccess;
  wasInSelection: boolean;
};

const buildClipboardNotification = (
  previous: AppAccessClipboardNotification | null,
  submodule: DashboardSelectorSubmoduleItem,
  appAccess: AppAccess,
  action: AppAccessClipboardNotification['action'],
): AppAccessClipboardNotification => ({
  key: (previous?.key ?? 0) + 1,
  appCount: appAccess.filters?.length ?? 0,
  appAccessType: appAccess.type,
  submoduleName: submodule.name,
  action,
});

export const useCopyAppAccessClipboard = ({
  setSelectedSubmodules,
  filterRegistry,
  onPasteError,
}: UseAppAccessClipboardParams) => {
  const [clipboardData, setClipboardData] = useState<DashboardSelectorSubmoduleItem | null>(null);
  const [pastedSubmoduleIds, setPastedSubmoduleIds] = useState<Set<string>>(new Set());
  const [previousPasteSnapshot, setPreviousPasteSnapshot] = useState<PreviousPasteSnapshot | null>(
    null,
  );
  const [clipboardNotification, setClipboardNotification] =
    useState<AppAccessClipboardNotification | null>(null);

  const isPasteEnabled = clipboardData !== null;

  const clipboardDataSubmoduleId = useMemo(() => clipboardData?.submoduleId, [clipboardData]);

  const resetClipboard = useCallback(() => {
    setPastedSubmoduleIds(new Set());
    setClipboardData(null);
  }, []);

  const clearClipboard = useCallback(() => {
    if (!clipboardData) return;
    resetClipboard();
  }, [clipboardData, resetClipboard]);

  const dismissClipboardNotification = useCallback(() => {
    setClipboardNotification(null);
  }, []);

  const copyAppAccess = useCallback((submodule: DashboardSelectorSubmoduleItem) => {
    if (!submodule.appAccess) return;
    setPastedSubmoduleIds(new Set());
    setClipboardData(submodule);
    setClipboardNotification((previous) =>
      buildClipboardNotification(previous, submodule, submodule.appAccess!, 'copied'),
    );
  }, []);

  const pasteAppAccess = useCallback(
    (submodule: DashboardSelectorSubmoduleItem) => {
      if (!clipboardData?.appAccess) return;
      const sourceFilters = clipboardData.appAccess.filters ?? [];
      let pastedAppAccess = clipboardData.appAccess;

      if (clipboardData.appAccess.type === 'CUSTOM' && filterRegistry) {
        if (!isPasteAllowed(sourceFilters, submodule.submoduleId, filterRegistry)) {
          onPasteError?.(`Cannot paste: selected app(s) are not available in ${submodule.name}.`);
          return;
        }

        const translatedFilters = translateFilters(
          sourceFilters,
          submodule.submoduleId,
          filterRegistry,
        );
        pastedAppAccess = { ...clipboardData.appAccess, filters: translatedFilters };
      }

      let undoSnapshot: PreviousPasteSnapshot | null = null;

      setSelectedSubmodules((previous) => {
        const existingSelection = previous.find(
          (item) => item.submoduleId === submodule.submoduleId,
        );

        undoSnapshot = {
          submoduleId: submodule.submoduleId,
          submodule,
          previousAppAccess: existingSelection?.appAccess,
          wasInSelection: Boolean(existingSelection),
        };

        if (!existingSelection) {
          return [...previous, { ...submodule, appAccess: pastedAppAccess }];
        }

        return previous.map((item) =>
          item.submoduleId === submodule.submoduleId
            ? { ...item, appAccess: pastedAppAccess }
            : item,
        );
      });

      setPreviousPasteSnapshot(undoSnapshot);
      setPastedSubmoduleIds((previous) => new Set([...previous, submodule.submoduleId]));
      setClipboardNotification((previous) =>
        buildClipboardNotification(previous, submodule, pastedAppAccess, 'pasted'),
      );
    },
    [clipboardData, filterRegistry, onPasteError, setSelectedSubmodules],
  );

  const undoPasteAppAccess = useCallback(() => {
    if (!previousPasteSnapshot) return;

    const { submoduleId, previousAppAccess, wasInSelection } = previousPasteSnapshot;

    setPastedSubmoduleIds((previous) => {
      const next = new Set(previous);
      next.delete(submoduleId);
      return next;
    });

    setSelectedSubmodules((previous) => {
      if (!wasInSelection) {
        return previous.filter((item) => item.submoduleId !== submoduleId);
      }

      return previous.map((item) =>
        item.submoduleId === submoduleId ? { ...item, appAccess: previousAppAccess } : item,
      );
    });

    setPreviousPasteSnapshot(null);
    setClipboardNotification(null);
  }, [previousPasteSnapshot, setSelectedSubmodules]);

  return {
    clipboardData,
    pastedSubmoduleIds,
    clipboardNotification,
    isPasteEnabled,
    clipboardDataSubmoduleId,
    copyAppAccess,
    pasteAppAccess,
    undoPasteAppAccess,
    resetClipboard,
    clearClipboard,
    dismissClipboardNotification,
  };
};
